import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_URL = 'https://martingale-strategy-calc.vercel.app/';
const DEFAULT_IMAGE = 'https://martingale-strategy-calc.vercel.app/logo512.png';

const MetaTags = ({ 
  title = "Martingale Strategy Calculator | Free Roulette & Betting Simulator",
  description = "Free online Martingale strategy calculator for roulette and casino betting. Calculate betting progressions, bankroll requirements, risk of ruin, and test your martingale system.",
  keywords = "martingale betting calculator, martingale strategy calculator, martingale system calculator, martingale strategy roulette calculator, martingale calculator roulette, roulette martingale calculator, martingale roulette calculator, grand martingale calculator, roulette strategy, betting calculator, bankroll management",
  canonical = DEFAULT_URL,
  ogTitle = "",
  ogDescription = "",
  ogImage = ""
}) => {
  // Safe origin resolution for SSR / Prerendering environments
  const siteUrl = typeof window !== 'undefined' && window.location ? window.location.href : DEFAULT_URL;
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalOgImage = ogImage || DEFAULT_IMAGE;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:site_name" content="Martingale Strategy Calculator" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={siteUrl} />
      <meta property="twitter:title" content={finalOgTitle} />
      <meta property="twitter:description" content={finalOgDescription} />
      <meta property="twitter:image" content={finalOgImage} />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content="English" />
      <meta name="author" content="Martingale Strategy Calculator" />
    </Helmet>
  );
};

// Predefined meta configurations with high search intent keywords
export const META_CONFIGS = {
  HOME: {
    title: "Martingale Strategy Calculator | Free Roulette & Betting Simulator",
    description: "Free online Martingale strategy calculator for roulette and casino betting. Calculate betting progressions, bankroll requirements, risk of ruin, and test your martingale system.",
    keywords: "martingale betting calculator, martingale strategy calculator, martingale system calculator, martingale strategy roulette calculator, martingale calculator roulette, roulette martingale calculator, martingale roulette calculator, grand martingale calculator, roulette strategy, betting calculator, bankroll management"
  },
  BLOG: {
    title: "How the Martingale System Works | Roulette & Betting Strategy Guide",
    description: "Learn how the Martingale and Grand Martingale betting systems work, their mathematical expectation, risks, and rewards in roulette and casino games.",
    keywords: "martingale system roulette, betting strategy guide, how martingale works, grand martingale system, gambling strategy, roulette bankroll management"
  },
  SIMULATION: {
    title: "Martingale Monte Carlo Simulator | Test Roulette Betting Progression",
    description: "Run Monte Carlo simulations on the Martingale betting strategy. Analyze win probabilities, maximum drawdowns, consecutive loss streaks, and bankroll survival rates.",
    keywords: "martingale simulation, monte carlo betting simulator, roulette simulator, martingale probability calculator, betting progression test"
  },
  RISK_ANALYSIS: {
    title: "Martingale Risk of Ruin Analysis | Casino Betting Risk Assessment",
    description: "Comprehensive risk analysis for the Martingale strategy. Calculate table limits impact, consecutive loss probabilities, house edge drag, and risk of ruin.",
    keywords: "martingale risk analysis, risk of ruin calculator, roulette risk calculator, gambling risk assessment, martingale bankroll safety"
  }
};

export default MetaTags;